import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  Container, Paper, TextField, Button, Typography, Box,
  CircularProgress, Snackbar
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import { fetchInvite, activateInvite } from "app/api/auth.api";
import { signIn } from "modules/login/redux/session.action";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f5f5f5",
  },
  paper: { padding: theme.spacing(4), width: 460 },
  status: { padding: theme.spacing(6), textAlign: "center" },
  form: { marginTop: theme.spacing(2) },
  submit: { marginTop: theme.spacing(3) },
  hint: { marginTop: theme.spacing(1) },
}));

const PASSWORD_REGEX =
  /^(?=.{8,}$)(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[0-9])(?=.*[!@#$%^&~`*()_+\-={}[\]:";'<>,.?|]).*$/;

function useQueryParams() {
  return new URLSearchParams(useLocation().search);
}

export default function SetupPasswordPage() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const params = useQueryParams();
  const uuid = params.get("uuid");
  const token = params.get("token");

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!uuid || !token) {
      setLoadError("Invalid activation link.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await fetchInvite({ uuid, token });
        if (!cancelled) {
          setInvite(data.data);
          setLoading(false);
        }
      } catch (e) {
        if (cancelled) return;
        const status = e.response?.status;
        const message =
          status === 410
            ? "This activation link has expired. Please ask your admin to resend the invitation."
            : e.response?.data?.message || "Invalid activation link.";
        setLoadError(message);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uuid, token]);

  const validate = () => {
    if (!PASSWORD_REGEX.test(password)) {
      return "Password must be at least 8 characters and contain lowercase, uppercase, digit, and special character.";
    }
    if (password !== confirmPassword) {
      return "Passwords do not match.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await activateInvite({ uuid, token, password, confirm_password: confirmPassword });
      const result = await dispatch(signIn({ email: invite.email, password }));
      if (result.success) {
        history.push("/portal");
      } else {
        history.push("/login");
      }
    } catch (e) {
      const message = e.response?.data?.message || "Failed to activate account. Please try again.";
      setSubmitError(message);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={classes.root}>
        <Paper className={classes.status}><CircularProgress /></Paper>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={classes.root}>
        <Paper className={classes.status}>
          <Typography variant="h6" color="error">{loadError}</Typography>
        </Paper>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Container maxWidth="sm">
        <Paper className={classes.paper} elevation={3}>
          <Typography variant="h5" align="center" gutterBottom>Set Up Your Account</Typography>
          <Typography variant="body2" align="center" color="textSecondary">
            Welcome <b>{invite.name}</b>. You're activating your account for <b>{invite.partner_name}</b>.
          </Typography>
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">Email: {invite.email}</Typography>
          </Box>
          <form className={classes.form} onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="New Password"
              type="password"
              margin="normal"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              margin="normal"
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <Typography variant="caption" color="textSecondary" className={classes.hint}>
              Password must be at least 8 characters with lowercase, uppercase, digit, and special character.
            </Typography>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              className={classes.submit}
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={24} /> : "Activate Account"}
            </Button>
          </form>
        </Paper>
        <Snackbar open={!!submitError} autoHideDuration={5000} onClose={() => setSubmitError(null)}>
          <Alert severity="error" onClose={() => setSubmitError(null)}>{submitError}</Alert>
        </Snackbar>
      </Container>
    </div>
  );
}
